import { Helpers } from '../helpers';
import { useStoreContext } from '../../store/store';
import { IApiResponse } from '../constants/interfaces';
import { useCallback, useMemo } from 'react';

export const useApi = () => {
  const { user, actions, dispatch } = useStoreContext();

  const sendRequest = useCallback(
    async <T>(
      endpoint: string,
      method = 'GET',
      body?: string | File | null | undefined,
      contentType = 'application/json'
    ): Promise<IApiResponse<T>> => {
      const apiUrl = import.meta.env.VITE_APP_API_URL ?? '';
      dispatch(actions.setUserLastActivity(new Date()));
      try {
        let headers: HeadersInit = {
          'Content-Type': contentType
        };

        let postBody: string | File | FormData | null | undefined = body;
        if (body instanceof File) {
          postBody = new FormData();
          postBody.append('file', body);
          headers = {};
        }

        if (user.token) {
          headers.Authorization = `Bearer ${user.token}`;
        }

        const requestOptions: RequestInit = {
          method: method,
          headers: headers,
          body: postBody
        };

        const apiResponse = await fetch(`${apiUrl}${endpoint}`, requestOptions);
        switch (apiResponse.status) {
          case 200: {
            const apiResp = (await apiResponse.json()) as IApiResponse<T>;
            apiResp.isSuccessStatusCode = true;
            return apiResp;
          }
          case 401:
          case 403:
            if (window.location.pathname.startsWith('/public-portal')) {
              window.location.href = '/token-expired';
            } else if (
              window.location.pathname !== '/' &&
              window.location.pathname.toLowerCase() !== '/login' &&
              window.location.pathname.toLowerCase() !== '/dev-tools/api'
            ) {
              window.location.href = '/login';
            }
            return Helpers.createApiResponse<T>(apiResponse.status, apiResponse.statusText);
          default:
            return Helpers.createApiResponse<T>(apiResponse.status, apiResponse.statusText);
        }
      } catch (e) {
        const errorText = Helpers.getErrorMessage(e);
        return Helpers.createApiResponse<T>(500, errorText);
      }
    },
    [actions, dispatch, user.token]
  );

  const get = useCallback(
    async <T>(endpoint: string): Promise<IApiResponse<T>> => {
      return await sendRequest<T>(endpoint, 'GET');
    },
    [sendRequest]
  );

  const post = useCallback(
    async <T>(
      endpoint: string,
      body: string,
      contentType = 'application/json'
    ): Promise<IApiResponse<T>> => {
      return await sendRequest<T>(endpoint, 'POST', body, contentType);
    },
    [sendRequest]
  );

  const patch = useCallback(
    async <T>(
      endpoint: string,
      body: string,
      contentType = 'application/json'
    ): Promise<IApiResponse<T>> => {
      return await sendRequest<T>(endpoint, 'PATCH', body, contentType);
    },
    [sendRequest]
  );

  const put = useCallback(
    async <T>(
      endpoint: string,
      body: string,
      contentType = 'application/json'
    ): Promise<IApiResponse<T>> => {
      return await sendRequest<T>(endpoint, 'PUT', body, contentType);
    },
    [sendRequest]
  );

  const del = useCallback(
    async <T>(endpoint: string): Promise<IApiResponse<T>> => {
      return await sendRequest<T>(endpoint, 'DELETE');
    },
    [sendRequest]
  );

  const postFile = useCallback(
    async <T>(endpoint: string, body: File): Promise<IApiResponse<T>> => {
      return await sendRequest<T>(endpoint, 'POST', body, '');
    },
    [sendRequest]
  );

  const upload = useCallback(
    async <T>(
      endpoint: string,
      file: File,
      onProgress?: (percentage: number) => void | null,
      onComplete?: (success: boolean, response: IApiResponse<T>) => void | null
    ): Promise<void> => {
      const xhr = new XMLHttpRequest();

      const handleProgressUpdate = (event: ProgressEvent<XMLHttpRequestEventTarget>) => {
        if (event.lengthComputable && onProgress) {
          const percentage = Helpers.round((event.loaded / event.total) * 100, 1);
          onProgress(percentage);
        }
      };

      await new Promise((resolve) => {
        xhr.upload.addEventListener('progress', handleProgressUpdate);
        xhr.addEventListener('progress', handleProgressUpdate);
        xhr.addEventListener('loadend', () => {
          const success = xhr.readyState === 4 && xhr.status === 200;
          let parsedResponse: IApiResponse<T> | null = null;
          try {
            parsedResponse = JSON.parse(xhr.response as string) as IApiResponse<T>;
          } catch {
            parsedResponse = Helpers.createApiResponse<T>(500, 'Unable to parse response.');
          }
          if (onComplete) onComplete(success, parsedResponse);
          resolve(success);
        });

        const formData = new FormData();
        formData.append(`files[0]`, file);

        const apiUrl = import.meta.env.VITE_APP_API_URL ?? '';
        xhr.open('POST', `${apiUrl}${endpoint}`, true);
        if (user.token) {
          xhr.setRequestHeader('Authorization', `Bearer ${user.token}`);
        }
        xhr.send(formData);
      });
    },
    [user.token]
  );

  return useMemo(
    () => ({
      get,
      post,
      put,
      patch,
      delete: del,
      upload,
      postFile,
      sendRequest,
      apiUrl: import.meta.env.VITE_APP_API_URL ?? ''
    }),
    [del, get, post, put, patch, upload, postFile, sendRequest]
  );
};
